import { useState, useEffect, useRef, useId } from 'react'
import { useUnsavedChangesWarning } from '../../hooks/useUnsavedChangesWarning'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { settingsService, OptimizationSettings } from '../../services/settings.service'
import { CogIcon, AdjustmentsHorizontalIcon, CheckCircleIcon, ScaleIcon, TruckIcon } from '@heroicons/react/24/outline'
import { MeasurementSystemSelect } from '../../components/MeasurementSystemSelect'
import { useAccess } from '../../hooks/useAccess'
import AdministrationPage, { AdministrationTabs } from '../../components/brand/AdministrationPage'
import Dialog from '../../components/ui/Dialog'
import { Save, RotateCcw } from 'lucide-react'

export default function Settings() {
  const access = useAccess()
  const rights = access.resource('optimization_settings')
  const [activeTab, setActiveTab] = useState('measurement')
  const panelId = useId()
  const formRef = useRef<HTMLFormElement>(null)
  const pristine = useRef<OptimizationSettings | null>(null)
  const dirty = useRef(false)
  const [presetEditorOpen, setPresetEditorOpen] = useState(false)

  const tabs = [
    { id: 'measurement', name: 'Unités de mesure', icon: ScaleIcon },
    { id: 'vehicles', name: 'Flotte', icon: TruckIcon },
    { id: 'limits', name: 'Limites', icon: CogIcon },
    { id: 'thresholds', name: 'Seuils et tolérances', icon: AdjustmentsHorizontalIcon },
    { id: 'weights', name: 'Contraintes', icon: ScaleIcon },
    { id: 'scoring', name: 'Stratégies', icon: ScaleIcon }
  ]
  const queryClient = useQueryClient()
  const [formData, setFormData] = useState<OptimizationSettings>({})
  const [savedMessage, setSavedMessage] = useState(false)
  const [activePresetId, setActivePresetId] = useState<string | null>(null)
  const [hasUnsavedChanges, setDirty] = useState(false)
  const setHasUnsavedChanges = (value: boolean) => { dirty.current = value; setDirty(value); if (value) setSavedMessage(false) }
  useUnsavedChangesWarning(hasUnsavedChanges)
  const [scoreImpact, setScoreImpact] = useState<{ totalImpact: string; impacts: Array<any> } | null>(null)

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['settings'],
    queryFn: settingsService.getSettings,
    enabled: rights.read,
    retry: false
  })

  useEffect(() => {
    if (data && !dirty.current) {
      const nextData: OptimizationSettings = {
        measurement_system: data.tenantConfig?.measurement_system ?? data.defaults?.measurement_system ?? 'metric',
        vehicle_registration_required: data.tenantConfig?.vehicle_registration_required ?? data.defaults?.vehicle_registration_required ?? false,
        limits: {
          ...(data.defaults?.limits || {}),
          ...(data.tenantConfig?.limits || {}),
        },
        thresholds: {
          stability: {
            minSupportRatio: 0.6,
            maxCenterOfGravityDeviation: 0.2,
            ...(data.defaults?.thresholds?.stability || {}),
            ...(data.tenantConfig?.thresholds?.stability || {}),
          },
          axleLoadSafetyMarginPercent: 0.05,
          ...(data.defaults?.thresholds || {}),
          ...(data.tenantConfig?.thresholds || {})
        },
        constraintWeights: {
          stability: 1,
          weight: 1,
          adr: 1,
          stackability: 1,
          rotation: 1,
          accessibility: 1,
          centerOfGravity: 1,
          // dynamicStability remplacé par les 3 métriques atomiques
          rollover: 1,
          yaw: 0.5,
          roll: 0.8,
          penaltyScale: 1,
          ...(data.defaults?.constraintWeights || {}),
          ...(data.tenantConfig?.constraintWeights || {})
        },
        optimizationWeights: {
          utilization: 0.25,
          stability: 0.15,
          // dynamicStability: indicateur composite agrégé (rollover+yaw+roll), NON pondéré
          rollover: 0.05,
          yaw: 0.03,
          roll: 0.04,
          weightDistribution: 0.15,
          accessibility: 0.10,
          centerOfGravity: 0.10,
          stackability: 0.05,
          rotationPreference: 0.05,
          ...(data.defaults?.optimizationWeights || {}),
          ...(data.tenantConfig?.optimizationWeights || {})
        },
        optimizationPresets: {
          ...(data.defaults?.optimizationPresets || {}),
          ...(data.tenantConfig?.optimizationPresets || {})
        },
        defaultPreset: data.tenantConfig?.defaultPreset ?? data.defaults?.defaultPreset ?? 'standard'
      }
      pristine.current = structuredClone(nextData)
      setFormData(nextData)
      setActivePresetId(data.tenantConfig?.defaultPreset ?? data.defaults?.defaultPreset ?? 'standard')
      setHasUnsavedChanges(false)
    }
  }, [data])

  // Simuler l'impact sur le score quand les poids changent
  useEffect(() => {
    const simulateImpact = async () => {
      if (!rights.manage || !formData.optimizationWeights || !activePresetId) return

      const presetWeights = data?.defaults?.optimizationPresets?.[activePresetId]?.weights ??
                          data?.tenantConfig?.optimizationPresets?.[activePresetId]?.weights ??
                          data?.defaults?.optimizationWeights

      if (!presetWeights) return

      try {
        const result = await settingsService.simulateScoreImpact(presetWeights, formData.optimizationWeights)
        setScoreImpact(result)
      } catch (error) {
        console.error('Error simulating score impact:', error)
      }
    }

    const debounceTimer = setTimeout(() => {
      simulateImpact()
    }, 500)

    return () => clearTimeout(debounceTimer)
  }, [formData.optimizationWeights, activePresetId, data, rights.manage])

  const mutation = useMutation({
    mutationFn: settingsService.updateSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] })
      setSavedMessage(true)
      setHasUnsavedChanges(false)
      setTimeout(() => setSavedMessage(false), 3000)
    }
  })

  const handleChange = (section: 'limits' | 'thresholds' | 'constraintWeights' | 'optimizationWeights', subSection: string | null, field: string, value: any) => {
    setHasUnsavedChanges(true)
    setFormData(prev => {
      const newData = { ...prev }
      if (section === 'limits') {
        newData.limits = { ...newData.limits, [field]: value }
      } else if (section === 'thresholds' && subSection) {
        newData.thresholds = {
          ...newData.thresholds,
          [subSection]: {
            // @ts-ignore
            ...newData.thresholds?.[subSection],
            [field]: value
          }
        }
      } else if (section === 'thresholds' && !subSection) {
        newData.thresholds = {
          ...newData.thresholds,
          [field]: value
        }
      } else if (section === 'constraintWeights') {
        newData.constraintWeights = {
          ...newData.constraintWeights,
          [field]: value
        }
      } else if (section === 'optimizationWeights') {
        newData.optimizationWeights = {
          ...newData.optimizationWeights,
          [field]: value
        }
        setHasUnsavedChanges(true)
      }
      return newData
    })
  }

  const handleSave = () => {
    if (!rights.manage || mutation.isLoading || !dirty.current || !formRef.current?.reportValidity()) return
    // Si un preset est actif, mettre à jour ses poids
    const settingsToSave = structuredClone(formData)
    if (activePresetId && settingsToSave.optimizationPresets?.[activePresetId]) {
      settingsToSave.optimizationPresets[activePresetId].weights = settingsToSave.optimizationWeights || {}
    }
    mutation.mutate(settingsToSave)
  }

  const getTotalWeight = () => {
    const weights = formData.optimizationWeights || {}
    return (
      (weights.utilization || 0) +
      (weights.stability || 0) +
      // dynamicStability: indicateur composite, NON inclus dans la somme des poids
      (weights.rollover || 0) +
      (weights.yaw || 0) +
      (weights.roll || 0) +
      (weights.weightDistribution || 0) +
      (weights.accessibility || 0) +
      (weights.centerOfGravity || 0) +
      (weights.stackability || 0) +
      (weights.rotationPreference || 0)
    )
  }

  const applyPreset = (presetId: string) => {
    const presets = formData.optimizationPresets || {}
    const preset = presets[presetId]

    if (!preset || !preset.weights) return

    // Si modifications non sauvegardées, demander confirmation
    if (hasUnsavedChanges && activePresetId !== presetId) {
      if (!confirm('Vous avez des modifications non sauvegardées. Voulez-vous les perdre en appliquant ce preset ?')) {
        return
      }
    }

    setFormData(prev => ({
      ...prev,
      optimizationWeights: preset.weights,
      defaultPreset: presetId
    }))
    setActivePresetId(presetId)
    setHasUnsavedChanges(true)
  }

  const setDefaultPreset = (presetId: string) => {
    setHasUnsavedChanges(true)
    setFormData(prev => ({
      ...prev,
      defaultPreset: presetId
    }))
  }

  const saveCustomPreset = (name: string, description: string, icon: string) => {
    setHasUnsavedChanges(true)
    const presetId = `custom_${Date.now()}`
    setFormData(prev => ({
      ...prev,
      optimizationPresets: {
        ...prev.optimizationPresets,
        [presetId]: {
          name,
          description,
          icon,
          weights: prev.optimizationWeights || {}
        }
      }
    }))
  }

  const deletePreset = (presetId: string) => {
    setHasUnsavedChanges(true)
    setFormData(prev => {
      const newPresets = { ...prev.optimizationPresets }
      delete newPresets[presetId]

      // Si on supprime le preset par défaut, revenir à 'standard'
      const newDefault = prev.defaultPreset === presetId ? 'standard' : prev.defaultPreset

      return {
        ...prev,
        optimizationPresets: newPresets,
        defaultPreset: newDefault
      }
    })
  }

  // Valeurs par défaut des presets système
  const defaultSystemPresets: Record<string, Record<string, number>> = {
    standard: {
      utilization: 0.25,
      stability: 0.15,
      // dynamicStability: 0.12 redistribué → rollover: 0.05, yaw: 0.03, roll: 0.04
      rollover: 0.05,
      yaw: 0.03,
      roll: 0.04,
      weightDistribution: 0.15,
      accessibility: 0.10,
      centerOfGravity: 0.10,
      stackability: 0.05,
      rotationPreference: 0.05
    },
    security: {
      utilization: 0.15,
      stability: 0.25,
      // dynamicStability: 0.25 redistribué → rollover: 0.10, yaw: 0.05, roll: 0.10
      rollover: 0.10,
      yaw: 0.05,
      roll: 0.10,
      weightDistribution: 0.10,
      accessibility: 0.05,
      centerOfGravity: 0.05,
      stackability: 0.03,
      rotationPreference: 0.02
    },
    volume: {
      utilization: 0.45,
      stability: 0.10,
      // dynamicStability: 0.08 redistribué → rollover: 0.03, yaw: 0.02, roll: 0.03
      rollover: 0.03,
      yaw: 0.02,
      roll: 0.03,
      weightDistribution: 0.10,
      accessibility: 0.05,
      centerOfGravity: 0.07,
      stackability: 0.05,
      rotationPreference: 0.05
    },
    cold: {
      utilization: 0.15,
      stability: 0.30,
      // dynamicStability: 0.15 redistribué → rollover: 0.06, yaw: 0.04, roll: 0.05
      rollover: 0.06,
      yaw: 0.04,
      roll: 0.05,
      weightDistribution: 0.10,
      accessibility: 0.05,
      centerOfGravity: 0.07,
      stackability: 0.20,
      rotationPreference: 0.05
    },
    adr: {
      utilization: 0.20,
      stability: 0.15,
      // dynamicStability: 0.25 redistribué → rollover: 0.10, yaw: 0.07, roll: 0.08
      rollover: 0.10,
      yaw: 0.07,
      roll: 0.08,
      weightDistribution: 0.10,
      accessibility: 0.20,
      centerOfGravity: 0.10,
      stackability: 0.05,
      rotationPreference: 0.05
    }
  }

  const restorePreset = (presetId: string) => {
    if (!defaultSystemPresets[presetId]) return

    if (confirm(`Restaurer le preset "${formData.optimizationPresets?.[presetId]?.name}" aux valeurs par défaut ?`)) {
      setHasUnsavedChanges(true)
      const defaultWeights = defaultSystemPresets[presetId]

      setFormData(prev => ({
        ...prev,
        optimizationWeights: defaultWeights,
        optimizationPresets: {
          ...prev.optimizationPresets,
          [presetId]: {
            ...prev.optimizationPresets![presetId],
            weights: defaultWeights
          }
        }
      }))

      if (activePresetId === presetId) {
        setHasUnsavedChanges(true)
      }
    }
  }

  const heading = { title: 'Paramètres', description: 'Définissez les valeurs par défaut de votre organisation et les paramètres de vos futurs calculs.' }
  if (access.isLoading) return <AdministrationPage {...heading}><p role="status" className="sl-card">Vérification de votre accès…</p></AdministrationPage>
  if (!rights.read) return <AdministrationPage {...heading}><p role="alert" className="sl-error">La consultation des paramètres nécessite une autorisation de lecture.</p></AdministrationPage>
  if (isLoading) return <AdministrationPage {...heading}><p role="status" className="sl-card">Chargement des paramètres…</p></AdministrationPage>
  if (isError) return <AdministrationPage {...heading}><div role="alert" className="sl-card space-y-4"><p>Les paramètres n’ont pas pu être chargés. Vos réglages enregistrés restent inchangés.</p><button className="sl-btn" onClick={() => void refetch()}>Réessayer</button></div></AdministrationPage>
  const discardChanges = () => {
    if (!pristine.current || !window.confirm('Annuler les modifications non enregistrées ?')) return
    setFormData(structuredClone(pristine.current)); setActivePresetId(pristine.current.defaultPreset || 'standard'); setHasUnsavedChanges(false)
  }
  return (
    <AdministrationPage {...heading} actions={<button type="submit" form={panelId} disabled={mutation.isLoading || !rights.manage || !hasUnsavedChanges} className="sl-btn sl-btn-primary"><Save size={16} aria-hidden="true" />{mutation.isLoading ? 'Sauvegarde…' : 'Sauvegarder'}</button>}>
      {!rights.manage && <p role="status" className="sl-notice">Consultation seule : vous pouvez parcourir les paramètres. Leur modification nécessite les droits de gestion.</p>}
      {savedMessage && (
        <div role="status" className="bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 p-4 rounded-xl flex items-center gap-3 animate-fade-in-down border border-green-200 dark:border-green-800">
          <CheckCircleIcon className="w-5 h-5" />
          <span>Paramètres sauvegardés avec succès.</span>
        </div>
      )}
      {mutation.isError && <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/30 dark:text-red-300">{(mutation.error as Error)?.message || 'Impossible de sauvegarder les paramètres.'}</div>}

      <AdministrationTabs tabs={tabs.map(tab => ({ id: tab.id, name: tab.name, icon: <tab.icon className="h-4 w-4" aria-hidden="true" /> }))} value={activeTab} onChange={setActiveTab} panelId={panelId} label="Sections des paramètres" />
      <form id={panelId} ref={formRef} noValidate role="tabpanel" tabIndex={0} aria-labelledby={`${panelId}-${activeTab}`} className="sl-card sl-settings-panel" onSubmit={event => { event.preventDefault(); handleSave() }} onChange={() => setHasUnsavedChanges(true)}>
          <fieldset disabled={!rights.manage || mutation.isLoading} className="min-w-0"><legend className="sr-only">Paramètres de l’entreprise</legend>
          {activeTab === 'measurement' && (
            <div className="space-y-6 animate-fade-in">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Unités de mesure par défaut</h2>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  Ce réglage appartient à votre organisation. Il initialise les nouveaux dossiers et s’applique aux catalogues produits, véhicules et ADR lorsqu’ils sont utilisés hors d’un dossier.
                </p>
              </div>

              <div className="max-w-xl rounded-xl border border-gray-200 bg-gray-50 p-5 dark:border-gray-700 dark:bg-gray-800/50">
                <MeasurementSystemSelect
                  value={formData.measurement_system ?? 'metric'}
                  onChange={(measurement_system) => {
                    setFormData(previous => ({ ...previous, measurement_system }))
                    setHasUnsavedChanges(true)
                  }}
                  disabled={mutation.isLoading || !rights.manage}
                />
                <p className="mt-3 text-xs leading-5 text-gray-500 dark:text-gray-400">
                  Métrique : cm, m, kg, m³. Impérial : in, ft, lb, ft³. Les dossiers existants et leurs versions conservent leur propre choix ; cette modification ne convertit aucune donnée enregistrée et ne relance aucun calcul.
                </p>
              </div>

              <div className="rounded-xl border border-orange-200 bg-orange-50 p-4 text-sm text-orange-800 dark:border-orange-800 dark:bg-orange-950/30 dark:text-orange-300">
                Le défaut système reste Métrique lorsque l’entreprise n’a encore enregistré aucun choix.
              </div>
            </div>
          )}

          {activeTab === 'vehicles' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Création et import des véhicules</h2>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Choisissez les informations à renseigner lors de l’ajout de véhicules dans votre flotte.</p>
              </div>
              <label className="flex cursor-pointer items-start gap-4 rounded-xl border border-gray-200 bg-gray-50 p-5 dark:border-gray-700 dark:bg-gray-800/50">
                <input type="checkbox" role="switch" aria-label="Immatriculation obligatoire" checked={formData.vehicle_registration_required ?? false} disabled={mutation.isLoading || !rights.manage} className="mt-1 h-5 w-5 accent-orange-600" onChange={event => {
                  const required = event.target.checked
                  setFormData(previous => ({ ...previous, vehicle_registration_required: required }))
                  setHasUnsavedChanges(true)
                }} />
                <div>
                  <span className="font-semibold text-gray-900 dark:text-white">Immatriculation obligatoire</span>
                  <p className="mt-2 text-sm leading-relaxed text-gray-600 dark:text-gray-400">{formData.vehicle_registration_required ? 'Une immatriculation ou une référence est requise pour créer ou enregistrer un véhicule, y compris pendant les imports.' : 'Ajoutez des véhicules et importez plusieurs modèles sans saisir d’immatriculation. Vous pourrez la renseigner plus tard.'}</p>
                </div>
              </label>
              <p className="rounded-xl border border-orange-200 bg-orange-50 p-4 text-sm text-orange-800 dark:border-orange-800 dark:bg-orange-950/30 dark:text-orange-300">L’immatriculation est optionnelle par défaut. Ce réglage s’applique à toute votre entreprise ; les véhicules existants sont conservés.</p>
            </div>
          )}

          {activeTab === 'limits' && (
            <div className="space-y-8 animate-fade-in">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Limites Opérationnelles</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                  Définissez les limites de vos calculs pour maîtriser les volumes et les ressources utilisés.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <label htmlFor="setting-limits-max_items_per_job" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Articles maximum par job
                    </label>
                    <span className="text-gray-400 cursor-help" title="Limite le nombre de colis pouvant être optimisés dans un seul job. Prévient les surcharges de mémoire et garantit des temps de réponse acceptables. Ex: 1000 colis max pour un job standard.">
                      ℹ️
                    </span>
                  </div>
                  <input
                    id="setting-limits-max_items_per_job"
                    type="number"
                    value={formData.limits?.max_items_per_job ?? ''}
                    onChange={(e) => handleChange('limits', null, 'max_items_per_job', e.target.value === '' ? '' : parseInt(e.target.value))}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-shadow"
                  />
                  <p className="mt-1 text-xs text-gray-500">Défaut: 1000. Protection contre les surcharges mémoire.</p>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <label htmlFor="setting-limits-max_execution_time_ms" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Temps d'exécution max (ms)
                    </label>
                    <span className="text-gray-400 cursor-help" title="Timeout maximum pour l'algorithme d'optimisation (Beam Search/Hybride). Garantit un temps de réponse maximum pour l'utilisateur. Ex: 30000ms = 30 secondes.">
                      ℹ️
                    </span>
                  </div>
                  <input
                    id="setting-limits-max_execution_time_ms"
                    type="number"
                    value={formData.limits?.max_execution_time_ms ?? ''}
                    onChange={(e) => handleChange('limits', null, 'max_execution_time_ms', e.target.value === '' ? '' : parseInt(e.target.value))}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-shadow"
                  />
                  <p className="mt-1 text-xs text-gray-500">Défaut: 30000ms (30s). Timeout pour l'algorithme.</p>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <label htmlFor="setting-limits-max_cpu_budget" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Budget CPU (Noeuds max)
                    </label>
                    <span className="text-gray-400 cursor-help" title="Nombre maximum de noeuds explorés par l'algorithme Beam Search. Contrôle la profondeur de recherche et la qualité de la solution. Plus élevé = meilleure solution mais plus lent. Ex: 50000 noeuds pour 100+ items.">
                      ℹ️
                    </span>
                  </div>
                  <input
                    id="setting-limits-max_cpu_budget"
                    type="number"
                    value={formData.limits?.max_cpu_budget ?? ''}
                    onChange={(e) => handleChange('limits', null, 'max_cpu_budget', e.target.value === '' ? '' : parseInt(e.target.value))}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-shadow"
                  />
                  <p className="mt-1 text-xs text-gray-500">Défaut: 50000. Profondeur de recherche Beam Search.</p>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <label htmlFor="setting-limits-max_concurrent_jobs_per_tenant" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Calculs simultanés par organisation
                    </label>
                    <span className="text-gray-400 cursor-help" title="Nombre maximum d'optimisations pouvant s'exécuter simultanément pour une même organisation. Prévient la saturation des ressources serveur. Ex: 5 jobs en parallèle maximum par client.">
                      ℹ️
                    </span>
                  </div>
                  <input
                    id="setting-limits-max_concurrent_jobs_per_tenant"
                    type="number"
                    value={formData.limits?.max_concurrent_jobs_per_tenant ?? ''}
                    onChange={(e) => handleChange('limits', null, 'max_concurrent_jobs_per_tenant', e.target.value === '' ? '' : parseInt(e.target.value))}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-shadow"
                  />
                  <p className="mt-1 text-xs text-gray-500">Défaut: 5. Limite de parallélisme par client.</p>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <label htmlFor="setting-limits-max_free_spaces" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Espaces libres max
                    </label>
                    <span className="text-gray-400 cursor-help" title="Nombre maximum d'espaces libres conservés pendant le calcul. Une valeur trop basse peut empêcher le moteur d'explorer des emplacements pourtant disponibles. Laisser vide pour ne pas appliquer ce plafond.">
                      ℹ️
                    </span>
                  </div>
                  <input
                    id="setting-limits-max_free_spaces"
                    type="number"
                    value={formData.limits?.max_free_spaces ?? ''}
                    onChange={(e) => handleChange('limits', null, 'max_free_spaces', e.target.value === '' ? '' : parseInt(e.target.value))}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-shadow"
                  />
                  <p className="mt-1 text-xs text-gray-500">Vide = illimité. Réduit la mémoire, mais peut fragmenter la recherche.</p>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <label htmlFor="setting-limits-max_candidates_global" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Candidats globaux max
                    </label>
                    <span className="text-gray-400 cursor-help" title="Nombre maximum de candidats de placement générés pour toute l'optimisation. Une valeur trop basse peut arrêter la recherche avant que tous les colis soient évalués. Laisser vide pour ne pas appliquer ce plafond.">
                      ℹ️
                    </span>
                  </div>
                  <input
                    id="setting-limits-max_candidates_global"
                    type="number"
                    value={formData.limits?.max_candidates_global ?? ''}
                    onChange={(e) => handleChange('limits', null, 'max_candidates_global', e.target.value === '' ? '' : parseInt(e.target.value))}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-shadow"
                  />
                  <p className="mt-1 text-xs text-gray-500">Vide = illimité. Pour 40 colis, prévoir plusieurs milliers de candidats.</p>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <label htmlFor="setting-limits-max_memory_bytes" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Mémoire max (octets)
                    </label>
                    <span className="text-gray-400 cursor-help" title="Budget mémoire maximal autorisé pendant le calcul. Laisser vide pour ne pas appliquer ce plafond.">
                      ℹ️
                    </span>
                  </div>
                  <input
                    id="setting-limits-max_memory_bytes"
                    type="number"
                    value={formData.limits?.max_memory_bytes ?? ''}
                    onChange={(e) => handleChange('limits', null, 'max_memory_bytes', e.target.value === '' ? '' : parseInt(e.target.value))}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-shadow"
                  />
                  <p className="mt-1 text-xs text-gray-500">Vide = illimité. Utilisé comme coupe-circuit mémoire.</p>
                </div>
              </div>

              {/* Info box explaining operational limits */}
              <div className="mt-6 p-4 bg-orange-50 dark:bg-orange-900/20 rounded-xl border border-orange-200 dark:border-orange-800">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                  <span>🛡️</span> Protection Multi-Tenant
                </h4>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Ces limites protègent le système SaaS contre les abus et garantissent une performance équitable pour tous les tenants. Elles sont appliquées par ordre de priorité : scénario &gt; tenant &gt; défauts système.
                </p>
              </div>
            </div>
          )}

          {activeTab === 'thresholds' && (
            <div className="space-y-8 animate-fade-in">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Stabilité</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                  Configurez les seuils de stabilité géométrique pour garantir la sécurité du chargement.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <label htmlFor="setting-thresholds-minSupportRatio" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Ratio de support minimum
                      </label>
                      <span className="text-gray-400 cursor-help" title="Pourcentage minimum de la surface d'un colis qui doit être supportée par les colis en dessous. Plus élevé = plus stable mais moins flexible. Ex: 0.6 = 60% de la surface doit être supportée.">
                        ℹ️
                      </span>
                    </div>
                    <input
                    id="setting-thresholds-minSupportRatio"
                      type="range"
                      min="0"
                      max="1"
                      step="0.05"
                      value={formData.thresholds?.stability?.minSupportRatio ?? 0.6}
                      onChange={(e) => handleChange('thresholds', 'stability', 'minSupportRatio', parseFloat(e.target.value))}
                      className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between mt-1">
                      <span className="text-xs text-gray-500">0</span>
                      <span className="text-xs font-medium text-orange-600 dark:text-orange-400">
                        {((formData.thresholds?.stability?.minSupportRatio ?? 0.6) * 100).toFixed(0)}%
                      </span>
                      <span className="text-xs text-gray-500">100%</span>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">Défaut: 0.6 (60%). Surface supportée minimum.</p>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <label htmlFor="setting-thresholds-maxCenterOfGravityDeviation" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Déviation max du centre de gravité
                      </label>
                      <span className="text-gray-400 cursor-help" title="Distance maximale autorisée entre le centre de gravité et le centre géométrique du camion. Plus faible = plus stable. Ex: 0.2 = 20% de déviation max autorisée.">
                        ℹ️
                      </span>
                    </div>
                    <input
                    id="setting-thresholds-maxCenterOfGravityDeviation"
                      type="range"
                      min="0"
                      max="0.5"
                      step="0.05"
                      value={formData.thresholds?.stability?.maxCenterOfGravityDeviation ?? 0.2}
                      onChange={(e) => handleChange('thresholds', 'stability', 'maxCenterOfGravityDeviation', parseFloat(e.target.value))}
                      className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between mt-1">
                      <span className="text-xs text-gray-500">0</span>
                      <span className="text-xs font-medium text-orange-600 dark:text-orange-400">
                        {((formData.thresholds?.stability?.maxCenterOfGravityDeviation ?? 0.2) * 100).toFixed(0)}%
                      </span>
                      <span className="text-xs text-gray-500">50%</span>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">Défaut: 0.2 (20%). Tolérance de centrage du CG.</p>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Poids et Essieux</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                  Configurez les marges de sécurité pour les limites de poids réglementaires par essieu.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <label htmlFor="setting-thresholds-axleLoadSafetyMarginPercent" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Marge de sécurité essieux
                      </label>
                      <span className="text-gray-400 cursor-help" title="Marge de sécurité appliquée aux limites de poids d'essieux réglementaires. Réduit la charge maximale autorisée pour garantir la conformité. Ex: 0.05 = 5% de marge (11500kg max devient 10925kg).">
                        ℹ️
                      </span>
                    </div>
                    <input
                    id="setting-thresholds-axleLoadSafetyMarginPercent"
                      type="range"
                      min="0"
                      max="0.2"
                      step="0.01"
                      value={formData.thresholds?.axleLoadSafetyMarginPercent ?? 0.05}
                      onChange={(e) => handleChange('thresholds', null, 'axleLoadSafetyMarginPercent', parseFloat(e.target.value))}
                      className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between mt-1">
                      <span className="text-xs text-gray-500">0%</span>
                      <span className="text-xs font-medium text-orange-600 dark:text-orange-400">
                        {((formData.thresholds?.axleLoadSafetyMarginPercent ?? 0.05) * 100).toFixed(0)}%
                      </span>
                      <span className="text-xs text-gray-500">20%</span>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">Défaut: 0.05 (5%). Marge de sécurité réglementaire.</p>
                  </div>

                  <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                    <div className="flex items-start gap-3">
                      <input
                        id="tenant-enforce-axle-limits"
                        type="checkbox"
                        checked={formData.thresholds?.enforceAxleLimits !== false}
                        onChange={(e) => handleChange('thresholds', null, 'enforceAxleLimits', e.target.checked)}
                        className="mt-1 h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded dark:bg-gray-700 dark:border-gray-600"
                      />
                      <div>
                        <label htmlFor="tenant-enforce-axle-limits" className="block text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                          Rejeter les surcharges essieux / sellette
                        </label>
                        <p className="mt-1 text-xs text-gray-500">
                          Activé par défaut. Désactivez uniquement pour une planification indicative avec pénalité de score.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Info box explaining thresholds */}
              <div className="mt-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                  <span>⚖️</span> Seuils de Tolérance
                </h4>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Ces seuils définissent les limites de tolérance pour les contraintes physiques. Ils sont utilisés par les moteurs de stabilité et de validation pour déterminer si un placement est acceptable. Des seuils plus stricts améliorent la sécurité mais réduisent la flexibilité.
                </p>
              </div>
            </div>
          )}

          {activeTab === 'weights' && (
            <div className="space-y-8 animate-fade-in">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Poids des Contraintes (ConstraintEvaluationEngine)</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                  Ajustez l'impact de chaque type de contrainte sur le score final. Plus le poids est élevé, plus la violation pénalise le score. Ces poids mesurent la gravité relative d'une violation physique.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <label htmlFor="setting-constraintWeights-stability" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Stabilité
                    </label>
                    <span className="text-gray-400 cursor-help" title="Mesure la gravité d'une violation de stabilité géométrique. Plus le poids est élevé, plus une instabilité pénalise le score. Ex: Un colis mal supporté sera fortement pénalisé.">
                      ℹ️
                    </span>
                  </div>
                  <input
                    id="setting-constraintWeights-stability"
                    type="range"
                    min="0"
                    max="3"
                    step="0.1"
                    value={formData.constraintWeights?.stability ?? 1}
                    onChange={(e) => handleChange('constraintWeights', null, 'stability', parseFloat(e.target.value))}
                    className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between mt-1">
                    <span className="text-xs text-gray-500">0</span>
                    <span className="text-xs font-medium text-orange-600 dark:text-orange-400">
                      {(formData.constraintWeights?.stability ?? 1).toFixed(1)}
                    </span>
                    <span className="text-xs text-gray-500">3</span>
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <label htmlFor="setting-constraintWeights-weight" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Distribution Poids
                    </label>
                    <span className="text-gray-400 cursor-help" title="Impact de la mauvaise distribution de charge. Plus le poids est élevé, plus une distribution inégale pénalise. Ex: Charge concentrée sur un côté du camion.">
                      ℹ️
                    </span>
                  </div>
                  <input
                    id="setting-constraintWeights-weight"
                    type="range"
                    min="0"
                    max="3"
                    step="0.1"
                    value={formData.constraintWeights?.weight ?? 1}
                    onChange={(e) => handleChange('constraintWeights', null, 'weight', parseFloat(e.target.value))}
                    className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between mt-1">
                    <span className="text-xs text-gray-500">0</span>
                    <span className="text-xs font-medium text-orange-600 dark:text-orange-400">
                      {(formData.constraintWeights?.weight ?? 1).toFixed(1)}
                    </span>
                    <span className="text-xs text-gray-500">3</span>
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <label htmlFor="setting-constraintWeights-adr" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      ADR (Marchandises Dangereuses)
                    </label>
                    <span className="text-gray-400 cursor-help" title="Gravité des incompatibilités matières dangereuses. Plus le poids est élevé, plus une violation ADR pénalise. Ex: Matières incompatibles placées côte à côte.">
                      ℹ️
                    </span>
                  </div>
                  <input
                    id="setting-constraintWeights-adr"
                    type="range"
                    min="0"
                    max="3"
                    step="0.1"
                    value={formData.constraintWeights?.adr ?? 1}
                    onChange={(e) => handleChange('constraintWeights', null, 'adr', parseFloat(e.target.value))}
                    className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between mt-1">
                    <span className="text-xs text-gray-500">0</span>
                    <span className="text-xs font-medium text-orange-600 dark:text-orange-400">
                      {(formData.constraintWeights?.adr ?? 1).toFixed(1)}
                    </span>
                    <span className="text-xs text-gray-500">3</span>
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <label htmlFor="setting-constraintWeights-stackability" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Empilabilité
                    </label>
                    <span className="text-gray-400 cursor-help" title="Gravité des violations d'empilement. Plus le poids est élevé, plus un empilement impossible/dangereux pénalise. Ex: Colis fragile sous une charge lourde.">
                      ℹ️
                    </span>
                  </div>
                  <input
                    id="setting-constraintWeights-stackability"
                    type="range"
                    min="0"
                    max="3"
                    step="0.1"
                    value={formData.constraintWeights?.stackability ?? 1}
                    onChange={(e) => handleChange('constraintWeights', null, 'stackability', parseFloat(e.target.value))}
                    className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between mt-1">
                    <span className="text-xs text-gray-500">0</span>
                    <span className="text-xs font-medium text-orange-600 dark:text-orange-400">
                      {(formData.constraintWeights?.stackability ?? 1).toFixed(1)}
                    </span>
                    <span className="text-xs text-gray-500">3</span>
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <label htmlFor="setting-constraintWeights-rotation" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Rotation
                    </label>
                    <span className="text-gray-400 cursor-help" title="Impact du non-respect des préférences de rotation. Plus le poids est élevé, plus une rotation non-préférée pénalise. Ex: Colis orienté dans le mauvais sens.">
                      ℹ️
                    </span>
                  </div>
                  <input
                    id="setting-constraintWeights-rotation"
                    type="range"
                    min="0"
                    max="3"
                    step="0.1"
                    value={formData.constraintWeights?.rotation ?? 1}
                    onChange={(e) => handleChange('constraintWeights', null, 'rotation', parseFloat(e.target.value))}
                    className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between mt-1">
                    <span className="text-xs text-gray-500">0</span>
                    <span className="text-xs font-medium text-orange-600 dark:text-orange-400">
                      {(formData.constraintWeights?.rotation ?? 1).toFixed(1)}
                    </span>
                    <span className="text-xs text-gray-500">3</span>
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <label htmlFor="setting-constraintWeights-accessibility" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Accessibilité
                    </label>
                    <span className="text-gray-400 cursor-help" title="Gravité de la difficulté d'accès à la porte. Plus le poids est élevé, plus une mauvaise accessibilité pénalise. Ex: Colis bloquant l'accès au déchargement.">
                      ℹ️
                    </span>
                  </div>
                  <input
                    id="setting-constraintWeights-accessibility"
                    type="range"
                    min="0"
                    max="3"
                    step="0.1"
                    value={formData.constraintWeights?.accessibility ?? 1}
                    onChange={(e) => handleChange('constraintWeights', null, 'accessibility', parseFloat(e.target.value))}
                    className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between mt-1">
                    <span className="text-xs text-gray-500">0</span>
                    <span className="text-xs font-medium text-orange-600 dark:text-orange-400">
                      {(formData.constraintWeights?.accessibility ?? 1).toFixed(1)}
                    </span>
                    <span className="text-xs text-gray-500">3</span>
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <label htmlFor="setting-constraintWeights-centerOfGravity" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Centre de Gravité
                    </label>
                    <span className="text-gray-400 cursor-help" title="Impact d'un centre de gravité mal centré. Plus le poids est élevé, plus un CG décentré pénalise. Ex: Charge trop à l'avant ou à l'arrière.">
                      ℹ️
                    </span>
                  </div>
                  <input
                    id="setting-constraintWeights-centerOfGravity"
                    type="range"
                    min="0"
                    max="3"
                    step="0.1"
                    value={formData.constraintWeights?.centerOfGravity ?? 1}
                    onChange={(e) => handleChange('constraintWeights', null, 'centerOfGravity', parseFloat(e.target.value))}
                    className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between mt-1">
                    <span className="text-xs text-gray-500">0</span>
                    <span className="text-xs font-medium text-orange-600 dark:text-orange-400">
                      {(formData.constraintWeights?.centerOfGravity ?? 1).toFixed(1)}
                    </span>
                    <span className="text-xs text-gray-500">3</span>
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <label htmlFor="setting-constraintWeights-rollover" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Stabilité Dynamique (Rollover)
                    </label>
                    <span className="text-gray-400 cursor-help" title="Risque de basculement latéral en virage. Plus le poids est élevé, plus ce risque pénalise.">
                      ℹ️
                    </span>
                  </div>
                  <input
                    id="setting-constraintWeights-rollover"
                    type="range"
                    min="0"
                    max="3"
                    step="0.1"
                    value={formData.constraintWeights?.rollover ?? 1}
                    onChange={(e) => handleChange('constraintWeights', null, 'rollover', parseFloat(e.target.value))}
                    className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between mt-1">
                    <span className="text-xs text-gray-500">0</span>
                    <span className="text-xs font-medium text-orange-600 dark:text-orange-400">
                      {(formData.constraintWeights?.rollover ?? 1).toFixed(1)}
                    </span>
                    <span className="text-xs text-gray-500">3</span>
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <label htmlFor="setting-constraintWeights-yaw" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Stabilité Dynamique (Lacet/Yaw)
                    </label>
                    <span className="text-gray-400 cursor-help" title="Risque de dérapage latéral. Moins critique que le rollover mais pénalise en cas de déséquilibre.">
                      ℹ️
                    </span>
                  </div>
                  <input
                    id="setting-constraintWeights-yaw"
                    type="range"
                    min="0"
                    max="3"
                    step="0.1"
                    value={formData.constraintWeights?.yaw ?? 0.5}
                    onChange={(e) => handleChange('constraintWeights', null, 'yaw', parseFloat(e.target.value))}
                    className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between mt-1">
                    <span className="text-xs text-gray-500">0</span>
                    <span className="text-xs font-medium text-orange-600 dark:text-orange-400">
                      {(formData.constraintWeights?.yaw ?? 0.5).toFixed(1)}
                    </span>
                    <span className="text-xs text-gray-500">3</span>
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <label htmlFor="setting-constraintWeights-roll" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Stabilité Dynamique (Roulis/Roll)
                    </label>
                    <span className="text-gray-400 cursor-help" title="Risque de basculement longitudinal. Critique pour le freinage et l'accélération.">
                      ℹ️
                    </span>
                  </div>
                  <input
                    id="setting-constraintWeights-roll"
                    type="range"
                    min="0"
                    max="3"
                    step="0.1"
                    value={formData.constraintWeights?.roll ?? 0.8}
                    onChange={(e) => handleChange('constraintWeights', null, 'roll', parseFloat(e.target.value))}
                    className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between mt-1">
                    <span className="text-xs text-gray-500">0</span>
                    <span className="text-xs font-medium text-orange-600 dark:text-orange-400">
                      {(formData.constraintWeights?.roll ?? 0.8).toFixed(1)}
                    </span>
                    <span className="text-xs text-gray-500">3</span>
                  </div>
                </div>

                <div className="md:col-span-2">
                  <div className="flex items-center gap-2 mb-2">
                    <label htmlFor="setting-constraintWeights-penaltyScale" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Échelle de Pénalité (Multiplicateur Global)
                    </label>
                    <span className="text-gray-400 cursor-help" title="Multiplicateur global des pénalités. Amplifie ou atténue toutes les pénalités de contraintes simultanément. Valeur > 1 renforce les pénalités, valeur < 1 les atténue.">
                      ℹ️
                    </span>
                  </div>
                  <input
                    id="setting-constraintWeights-penaltyScale"
                    type="range"
                    min="0"
                    max="3"
                    step="0.1"
                    value={formData.constraintWeights?.penaltyScale ?? 1}
                    onChange={(e) => handleChange('constraintWeights', null, 'penaltyScale', parseFloat(e.target.value))}
                    className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between mt-1">
                    <span className="text-xs text-gray-500">0</span>
                    <span className="text-xs font-medium text-orange-600 dark:text-orange-400">
                      {(formData.constraintWeights?.penaltyScale ?? 1).toFixed(1)}x
                    </span>
                    <span className="text-xs text-gray-500">3x</span>
                  </div>
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    💡 Ce multiplicateur s'applique à toutes les pénalités de contraintes ci-dessus. Utilisez-le pour renforcer ou atténuer globalement l'impact des violations.
                  </p>
                </div>
              </div>

              {/* Info box explaining the difference between constraint weights and scoring weights */}
              <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                  <span>⚠️</span> Différence avec les Pondérations Stratégiques
                </h4>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Les <strong> poids des contraintes</strong> mesurent la gravité d'une violation physique (ex: instabilité, mauvaise distribution).<br />
                  Les <strong>pondérations stratégiques</strong> définissent vos priorités métier (ex: maximiser le remplissage vs privilégier la sécurité).<br />
                  Ces deux catégories sont complémentaires et interviennent à des moments différents du calcul du score.
                </p>
              </div>
            </div>
          )}

          {activeTab === 'scoring' && (
            <div className="space-y-8 animate-fade-in">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Priorités stratégiques du chargement</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                  Définissez les priorités stratégiques du chargement. Ces poids influencent directement le score global du plan d'optimisation.
                </p>

                {/* Presets */}
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Profils Pré-configurés
                    </label>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                    {Object.entries(formData.optimizationPresets || {}).map(([presetId, preset]) => {
                      const isDefault = formData.defaultPreset === presetId
                      const isCustom = presetId.startsWith('custom_')
                      return (
                        <div
                          key={presetId}
                          className={`relative p-3 rounded-xl border transition-all ${
                            isDefault
                              ? 'border-orange-500 dark:border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                              : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-orange-500 dark:hover:border-orange-500 hover:bg-orange-50 dark:hover:bg-orange-900/20'
                          }`}
                        >
                          <button type="button"
                            onClick={() => applyPreset(presetId)}
                            className="w-full text-left"
                          >
                            <div className="font-medium text-sm text-gray-900 dark:text-white flex items-center gap-2">
                              {preset.icon} {preset.name}
                              {isDefault && (
                                <span className="text-xs bg-orange-600 text-white px-1.5 py-0.5 rounded">Défaut</span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{preset.description}</div>
                          </button>
                          {isCustom && (
                            <button type="button"
                              onClick={(e) => {
                                e.stopPropagation()
                                if (confirm('Supprimer ce preset personnalisé ?')) {
                                  deletePreset(presetId)
                                }
                              }}
                              className="absolute top-2 right-2 text-gray-400 hover:text-red-500 transition-colors"
                              title="Supprimer"
                            >
                              ✕
                            </button>
                          )}
                          {!isCustom && (
                            <div className="absolute top-2 right-2 flex gap-1">
                              {!isDefault && (
                                <button type="button"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setDefaultPreset(presetId)
                                  }}
                                  className="text-gray-400 hover:text-orange-500 transition-colors"
                                  title="Définir par défaut"
                                >
                                  ⭐
                                </button>
                              )}
                              <button type="button"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  restorePreset(presetId)
                                }}
                                className="text-gray-400 hover:text-green-500 transition-colors"
                                title="Restaurer les valeurs par défaut"
                              >
                                ↺
                              </button>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>

                  {/* Bouton pour sauvegarder un preset personnalisé */}
                  <button type="button" onClick={() => setPresetEditorOpen(true)}
                    className="mt-3 px-4 py-2 text-sm text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 border border-dashed border-orange-300 dark:border-orange-700 rounded-lg hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-all"
                  >
                    + Créer un profil personnalisé
                  </button>
                </div>

                {/* Impact simulé sur le score */}
                {scoreImpact && scoreImpact.impacts.length > 0 && (
                  <div className="mt-6 p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                      <span>📊</span> Impact estimé sur le score global
                    </h4>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Impact total:</span>
                        <span className={`font-medium ${parseFloat(scoreImpact.totalImpact) > 0 ? 'text-green-600 dark:text-green-400' : parseFloat(scoreImpact.totalImpact) < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-400'}`}>
                          {parseFloat(scoreImpact.totalImpact) > 0 ? '+' : ''}{scoreImpact.totalImpact}%
                        </span>
                      </div>
                      {scoreImpact.impacts.slice(0, 3).map((impact: any) => (
                        <div key={impact.key} className="flex justify-between items-center text-xs">
                          <span className="text-gray-600 dark:text-gray-400 capitalize">
                            {impact.key.replace(/([A-Z])/g, ' $1').trim()}:
                          </span>
                          <span className={`font-medium ${parseFloat(impact.relativeChange) > 0 ? 'text-green-600 dark:text-green-400' : parseFloat(impact.relativeChange) < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-400'}`}>
                            {parseFloat(impact.relativeChange) > 0 ? '+' : ''}{impact.relativeChange}% → {parseFloat(impact.impactOnScore) > 0 ? '+' : ''}{impact.impactOnScore}%
                          </span>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      * Estimation basée sur un scénario type. L'impact réel peut varier selon la cargaison.
                    </p>
                  </div>
                )}

                {/* Sliders */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <label htmlFor="setting-optimizationWeights-utilization" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Compacité
                      </label>
                      <span className="text-gray-400 cursor-help" title="Maximise l'imbrication locale des colis et minimise l'espace vide entre eux. Priorité: réduction de la fragmentation de l'espace.">
                        ℹ️
                      </span>
                    </div>
                    <input
                    id="setting-optimizationWeights-utilization"
                      type="range"
                      min="0"
                      max="0.5"
                      step="0.01"
                      value={formData.optimizationWeights?.utilization ?? 0.25}
                      onChange={(e) => handleChange('optimizationWeights', null, 'utilization', parseFloat(e.target.value))}
                      className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between mt-1">
                      <span className="text-xs text-gray-500">0</span>
                      <span className="text-xs font-medium text-orange-600 dark:text-orange-400">
                        {((formData.optimizationWeights?.utilization ?? 0.25) * 100).toFixed(0)}%
                      </span>
                      <span className="text-xs text-gray-500">50%</span>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <label htmlFor="setting-optimizationWeights-stability" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Stabilité
                      </label>
                      <span className="text-gray-400 cursor-help" title="Assure que les colis ne basculent pas. Ex: Transport de produits fragiles ou lourds (moteurs, batteries). Priorité: support minimal et centre de gravité bas.">
                        ℹ️
                      </span>
                    </div>
                    <input
                    id="setting-optimizationWeights-stability"
                      type="range"
                      min="0"
                      max="0.3"
                      step="0.01"
                      value={formData.optimizationWeights?.stability ?? 0.15}
                      onChange={(e) => handleChange('optimizationWeights', null, 'stability', parseFloat(e.target.value))}
                      className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between mt-1">
                      <span className="text-xs text-gray-500">0</span>
                      <span className="text-xs font-medium text-orange-600 dark:text-orange-400">
                        {((formData.optimizationWeights?.stability ?? 0.15) * 100).toFixed(0)}%
                      </span>
                      <span className="text-xs text-gray-500">30%</span>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <label htmlFor="setting-optimizationWeights-weightDistribution" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Distribution Poids
                      </label>
                      <span className="text-gray-400 cursor-help" title="Équilibre la charge sur les essieux. Ex: Respect des limites légales de poids par essieu, conformité ADR. Priorité: répartition uniforme du poids.">
                        ℹ️
                      </span>
                    </div>
                    <input
                    id="setting-optimizationWeights-weightDistribution"
                      type="range"
                      min="0"
                      max="0.3"
                      step="0.01"
                      value={formData.optimizationWeights?.weightDistribution ?? 0.15}
                      onChange={(e) => handleChange('optimizationWeights', null, 'weightDistribution', parseFloat(e.target.value))}
                      className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between mt-1">
                      <span className="text-xs text-gray-500">0</span>
                      <span className="text-xs font-medium text-orange-600 dark:text-orange-400">
                        {((formData.optimizationWeights?.weightDistribution ?? 0.15) * 100).toFixed(0)}%
                      </span>
                      <span className="text-xs text-gray-500">30%</span>
                    </div>
                  </div>

                  <div className="md:col-span-3">
                    <div className="flex items-center gap-2 mb-3">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Stabilité Dynamique (Indicateur Composite)
                      </label>
                      <span className="text-gray-400 cursor-help" title="Indicateur agrégé composé de Rollover, Lacet (Yaw) et Roulis (Roll). Pondérez ses composantes directement ci-dessous.">
                        ℹ️
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
                      <div>
                        <label htmlFor="setting-optimizationWeights-rollover" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Rollover</label>
                        <input
                    id="setting-optimizationWeights-rollover"
                          type="range"
                          min="0"
                          max="0.15"
                          step="0.01"
                          value={formData.optimizationWeights?.rollover ?? 0.05}
                          onChange={(e) => handleChange('optimizationWeights', null, 'rollover', parseFloat(e.target.value))}
                          className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                        />
                        <div className="flex justify-between mt-1">
                          <span className="text-xs text-gray-500">0%</span>
                          <span className="text-xs font-medium text-orange-600 dark:text-orange-400">
                            {((formData.optimizationWeights?.rollover ?? 0.05) * 100).toFixed(0)}%
                          </span>
                          <span className="text-xs text-gray-500">15%</span>
                        </div>
                      </div>
                      <div>
                        <label htmlFor="setting-optimizationWeights-yaw" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Lacet (Yaw)</label>
                        <input
                    id="setting-optimizationWeights-yaw"
                          type="range"
                          min="0"
                          max="0.10"
                          step="0.01"
                          value={formData.optimizationWeights?.yaw ?? 0.03}
                          onChange={(e) => handleChange('optimizationWeights', null, 'yaw', parseFloat(e.target.value))}
                          className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                        />
                        <div className="flex justify-between mt-1">
                          <span className="text-xs text-gray-500">0%</span>
                          <span className="text-xs font-medium text-orange-600 dark:text-orange-400">
                            {((formData.optimizationWeights?.yaw ?? 0.03) * 100).toFixed(0)}%
                          </span>
                          <span className="text-xs text-gray-500">10%</span>
                        </div>
                      </div>
                      <div>
                        <label htmlFor="setting-optimizationWeights-roll" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Roulis (Roll)</label>
                        <input
                    id="setting-optimizationWeights-roll"
                          type="range"
                          min="0"
                          max="0.15"
                          step="0.01"
                          value={formData.optimizationWeights?.roll ?? 0.04}
                          onChange={(e) => handleChange('optimizationWeights', null, 'roll', parseFloat(e.target.value))}
                          className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                        />
                        <div className="flex justify-between mt-1">
                          <span className="text-xs text-gray-500">0%</span>
                          <span className="text-xs font-medium text-orange-600 dark:text-orange-400">
                            {((formData.optimizationWeights?.roll ?? 0.04) * 100).toFixed(0)}%
                          </span>
                          <span className="text-xs text-gray-500">15%</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <label htmlFor="setting-optimizationWeights-accessibility" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Accessibilité
                      </label>
                      <span className="text-gray-400 cursor-help" title="Facilite le déchargement selon l'ordre. Ex: Livraison multi-points avec ordre de déchargement imposé. Priorité: placement des colis selon leur séquence.">
                        ℹ️
                      </span>
                    </div>
                    <input
                    id="setting-optimizationWeights-accessibility"
                      type="range"
                      min="0"
                      max="0.2"
                      step="0.01"
                      value={formData.optimizationWeights?.accessibility ?? 0.10}
                      onChange={(e) => handleChange('optimizationWeights', null, 'accessibility', parseFloat(e.target.value))}
                      className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between mt-1">
                      <span className="text-xs text-gray-500">0</span>
                      <span className="text-xs font-medium text-orange-600 dark:text-orange-400">
                        {((formData.optimizationWeights?.accessibility ?? 0.10) * 100).toFixed(0)}%
                      </span>
                      <span className="text-xs text-gray-500">20%</span>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <label htmlFor="setting-optimizationWeights-centerOfGravity" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Centre de Gravité
                      </label>
                      <span className="text-gray-400 cursor-help" title="Positionne le centre de gravité au centre du camion. Ex: Transport de charges lourds, stabilité en virage. Priorité: CG proche du centre géométrique.">
                        ℹ️
                      </span>
                    </div>
                    <input
                    id="setting-optimizationWeights-centerOfGravity"
                      type="range"
                      min="0"
                      max="0.2"
                      step="0.01"
                      value={formData.optimizationWeights?.centerOfGravity ?? 0.10}
                      onChange={(e) => handleChange('optimizationWeights', null, 'centerOfGravity', parseFloat(e.target.value))}
                      className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between mt-1">
                      <span className="text-xs text-gray-500">0</span>
                      <span className="text-xs font-medium text-orange-600 dark:text-orange-400">
                        {((formData.optimizationWeights?.centerOfGravity ?? 0.10) * 100).toFixed(0)}%
                      </span>
                      <span className="text-xs text-gray-500">20%</span>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <label htmlFor="setting-optimizationWeights-stackability" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Empilabilité
                      </label>
                      <span className="text-gray-400 cursor-help" title="Maximise la hauteur des empilements. Ex: Transport de produits résistants à la compression (boîtes, caisses). Priorité: empilement vertical optimal.">
                        ℹ️
                      </span>
                    </div>
                    <input
                    id="setting-optimizationWeights-stackability"
                      type="range"
                      min="0"
                      max="0.15"
                      step="0.01"
                      value={formData.optimizationWeights?.stackability ?? 0.05}
                      onChange={(e) => handleChange('optimizationWeights', null, 'stackability', parseFloat(e.target.value))}
                      className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between mt-1">
                      <span className="text-xs text-gray-500">0</span>
                      <span className="text-xs font-medium text-orange-600 dark:text-orange-400">
                        {((formData.optimizationWeights?.stackability ?? 0.05) * 100).toFixed(0)}%
                      </span>
                      <span className="text-xs text-gray-500">15%</span>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <label htmlFor="setting-optimizationWeights-rotationPreference" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Rotation
                      </label>
                      <span className="text-gray-400 cursor-help" title="Autorise la rotation des colis pour optimiser l'espace. Ex: Colis non directionnels (cartons standards). Priorité: flexibilité d'orientation.">
                        ℹ️
                      </span>
                    </div>
                    <input
                    id="setting-optimizationWeights-rotationPreference"
                      type="range"
                      min="0"
                      max="0.15"
                      step="0.01"
                      value={formData.optimizationWeights?.rotationPreference ?? 0.05}
                      onChange={(e) => handleChange('optimizationWeights', null, 'rotationPreference', parseFloat(e.target.value))}
                      className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between mt-1">
                      <span className="text-xs text-gray-500">0</span>
                      <span className="text-xs font-medium text-orange-600 dark:text-orange-400">
                        {((formData.optimizationWeights?.rotationPreference ?? 0.05) * 100).toFixed(0)}%
                      </span>
                      <span className="text-xs text-gray-500">15%</span>
                    </div>
                  </div>

                  {/* Advanced weights removed to avoid duplication with composite indicator sliders above */}

                  {/* Total indicator */}
                  <div className="md:col-span-2 mt-4 p-4 bg-orange-50 dark:bg-orange-900/20 rounded-xl border border-orange-200 dark:border-orange-800">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Total des poids</span>
                      <span className={`text-lg font-bold ${getTotalWeight() === 1 ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>
                        {(getTotalWeight() * 100).toFixed(0)}%
                      </span>
                    </div>
                    {getTotalWeight() !== 1 && (
                      <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                        ⚠️ Le total devrait être proche de 100% pour un scoring cohérent
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          </fieldset>
      </form>
      {hasUnsavedChanges && rights.manage && <div className="sl-settings-actions"><span role="status">Modifications non enregistrées</span><div className="flex flex-wrap gap-2"><button type="button" className="sl-btn" disabled={mutation.isLoading} onClick={discardChanges}><RotateCcw size={15} />Annuler les modifications</button><button type="submit" form={panelId} className="sl-btn sl-btn-primary" disabled={mutation.isLoading}>Enregistrer les modifications</button></div></div>}
      {presetEditorOpen && <PresetEditor onClose={() => setPresetEditorOpen(false)} onCreate={(name, description, icon) => { saveCustomPreset(name, description, icon); setPresetEditorOpen(false) }} />}
    </AdministrationPage>
  )
}

function PresetEditor({ onClose, onCreate }: { onClose: () => void; onCreate: (name: string, description: string, icon: string) => void }) {
  const [name, setName] = useState(''), [description, setDescription] = useState(''), [icon, setIcon] = useState('⚙️')
  return <Dialog isOpen title="Créer un profil personnalisé" onClose={onClose}>
    <p className="sl-muted mb-5 text-sm">Le profil reprend les pondérations affichées. Sauvegardez ensuite les paramètres pour le conserver.</p>
    <form className="space-y-4" onSubmit={event => { event.preventDefault(); if (name.trim()) onCreate(name.trim(), description.trim() || 'Personnalisé', icon) }}>
      <label className="block text-sm font-medium">Nom du profil<input className="sl-field" required maxLength={60} value={name} onChange={event => setName(event.target.value)} /></label>
      <label className="block text-sm font-medium">Description<input className="sl-field" maxLength={240} value={description} onChange={event => setDescription(event.target.value)} /></label>
      <label className="block text-sm font-medium">Repère visuel<select className="sl-field" value={icon} onChange={event => setIcon(event.target.value)}>{['⚙️','📦','🚚','🛡️','❄️'].map(value => <option key={value}>{value}</option>)}</select></label>
      <div className="flex flex-wrap justify-end gap-2 pt-2"><button type="button" className="sl-btn" onClick={onClose}>Annuler</button><button className="sl-btn sl-btn-primary" disabled={!name.trim()}>Créer le profil</button></div>
    </form>
  </Dialog>
}
